import { logEvent, EVENTS } from 'lib/amplitude';
import { signOut, getOktaAuth } from 'lib/okta';
import LocalStorageProxy from 'lib/localStorageProxy';
import { isApplyNow } from 'lib/envHelper';
import {
  LOGIN_CAPTURE_PATH,
  GET_STARTED_PATH,
  HELP_PATH,
} from 'shared/constants/paths';
import { getAdvisorOrAdvisorOrg } from 'lib/prepareStore';
import { NZ_SLUG_REGEX } from 'shared/config/pathMatcher';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { isEmpty } from 'lodash';

export const logoutUtil = async ({
  overrideLogoutRedirectUri,
  oktaAuth = getOktaAuth(),
  showContinueSession = false,
  statusHandler = {},
}) => {
  logEvent(EVENTS.LOG_OUT);
  const validateLogoutRedirectUri = onLogoutRedirectPath({
    overrideLogoutRedirectUri,
  });

  await signOut(oktaAuth);

  if (validateLogoutRedirectUri) {
    LocalStorageProxy.logoutRedirectUri = validateLogoutRedirectUri;
  }

  if (showContinueSession) {
    LocalStorageProxy.showContinueSession = showContinueSession;
  }

  if (statusHandler.setSessionStatus) {
    statusHandler.setSessionStatus({ isContinue: false, timeLeft: 0 });
  }

  LocalStorageProxy.clearAll();
  sessionStorage.clear();
  window.location.href = validateLogoutRedirectUri;
  window.location.reload();
};

export const getStoragePath = (origin, pathname) => `${origin}${pathname}#`;

export const setLoginCurrentPath = () => {
  const { origin, pathname } = window.location;
  const redirectPath = getStoragePath(origin, pathname);
  LocalStorageProxy.loginCurrentPath = redirectPath;
};

export const validateLoginCurrentPath = () => {
  const { origin, pathname } = window.location;
  const currentPath = getStoragePath(origin, pathname);
  if (
    LocalStorageProxy.loginCurrentPath &&
    LocalStorageProxy.loginCurrentPath !== currentPath
  ) {
    window.location.replace(LocalStorageProxy.loginCurrentPath);
  }
};

export const onConstructURL = (path) => {
  const { advisorOrgId, advisorOrgSlug, advisorUuid } = getAdvisorOrAdvisorOrg(
    window.location.pathname,
  );

  const orgId = advisorOrgId ? `/${advisorOrgId}` : '';
  const slug = advisorOrgSlug ? `/${advisorOrgSlug}` : '';
  const uuid = advisorUuid ? `/${advisorUuid}` : '';

  const isNZ = window.location.pathname.match(NZ_SLUG_REGEX);
  const fullPath = `${window.location.origin}${isNZ ? '/nz' : ''}`;

  return `${fullPath}${orgId}${slug}${uuid}/#${path}`;
};

/**
 * Generic function that handles logout redirection to login
 * Reason: prevent users from accidentally registering as new clients
 * with different brokers after logging out or session expire from Milli.
 * Default redirect path is LOGIN_CAPTURE_PATH
 * If overrideLogoutRedirectUri is provided, it will redirect to the provided path
 * @see onLogoutRedirectPath
 */
export const onLogoutRedirectPath = ({ overrideLogoutRedirectUri }) => {
  if (overrideLogoutRedirectUri) {
    return overrideLogoutRedirectUri;
  }

  return onConstructURL(LOGIN_CAPTURE_PATH);
};

/*
  This function is used to restore the original URI after a logout.
  It is used in the goal-index.js file.
  It is used in index.js file.
  Transfered from goal-index.js and index.js files, to have one source of truth.
*/
export const restoreOriginalUri = async (_oktaAuth, originalUri) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const history = useHistory();
  history.replace(toRelativeUrl(originalUri, window.location.origin));
};

export const isBrokerLogin = () =>
  !!LocalStorageProxy.loginAsClientId ||
  !!LocalStorageProxy.loginAsLoanApplicationId;

export const refreshTokens = async (oktaAuth) => {
  try {
    // First try to refresh just the access token
    const refreshed = await oktaAuth.tokenManager.renew('accessToken');
    if (refreshed) {
      // Update local storage with new token
      LocalStorageProxy.token = refreshed.accessToken ?? '';
      // Trigger auth state update
      await oktaAuth.authStateManager.updateAuthState();
      return refreshed;
    }

    // If simple refresh fails, try getting new tokens
    const { tokens } = await oktaAuth.token.getWithoutPrompt({
      responseType: 'code',
      scopes: ['openid', 'profile', 'email'],
    });

    await oktaAuth.tokenManager.setTokens(tokens);
    LocalStorageProxy.token = tokens.accessToken?.accessToken ?? '';
    await oktaAuth.authStateManager.updateAuthState();
    return refreshed;
  } catch (error) {
    console.error('Failed to refresh tokens:', error);
    // If refresh fails completely, throw error for handling by caller
    await logoutUtil({
      oktaAuth,
    });
  }
};

/**
 * OLD function that handles logout redirection to login
 * currently this function is only used in the header component
 * @see onLogoutRedirectPath
 */
export const byobLogoutRedirectUrl = (isOwnBrand, isNonLMG) => {
  const pcfRedirectPath = isNonLMG ? HELP_PATH : '';
  const path =
    isApplyNow() && !isOwnBrand ? `${GET_STARTED_PATH}` : pcfRedirectPath;

  return onConstructURL(path);
};

export const checkTokenExpiration = () => {
  const mycrmTokens = localStorage.getItem('mycrm-tokens');
  const token = localStorage.getItem('token');
  const mycrmTokensObj = JSON.parse(mycrmTokens);

  const expiresAt = mycrmTokensObj?.accessToken?.expiresAt;
  const isValidToken = expiresAt * 1000 > Date.now();

  if (isBrokerLogin()) {
    return;
  }

  if (
    (isEmpty(mycrmTokensObj) && token) ||
    (!isValidToken && !isEmpty(mycrmTokensObj))
  ) {
    LocalStorageProxy.showContinueSession = true;
    LocalStorageProxy.clearAll();
    sessionStorage.clear();
    window.location.href = onLogoutRedirectPath(LOGIN_CAPTURE_PATH);
  }
};
